import { fmtHours, fmtTime, localYmd } from './portalTime';
import {
  PortalHourlyRow,
  PortalJob,
  PortalPayPeriod,
  PortalHoliday,
  PortalTimeOffRequest,
  TYPE_MEAL,
} from './portalTypes';
import type { PendingPunch } from './punchStore';

/**
 * Employee Time Portal — Today's pure model (P-TAS-20 Phase 3, App-1). Turns the
 * employee's TimeEntryHourly rows into what the Today page shows: the punch state
 * (out / in / meal / done), the punch list, worked and meal minutes, job segments,
 * which buttons are offered, the compliance hint and the status strip. No React, no
 * axios, no config: everything arrives as arguments so jest covers it directly.
 *
 * Rows follow the mobile V2 sequence the API mirrors: a meal closes the REG row and
 * opens a MEAL row at the same instant; ending the meal closes the MEAL row and opens a
 * REG row; a job switch closes a REG row and opens another REG row.
 */

export type PunchState = 'out' | 'in' | 'meal' | 'done';
export type PunchKind = 'in' | 'out' | 'meal-start' | 'meal-end' | 'meal2-start' | 'meal2-end';
export type SourceKind = 'web' | 'mobile' | 'kiosk' | 'admin' | 'system';

export const PUNCH_LABEL: Record<PunchKind, string> = {
  in: 'Clock In',
  out: 'Clock Out',
  'meal-start': 'Meal Start',
  'meal-end': 'Meal End',
  'meal2-start': '2nd Meal Start',
  'meal2-end': '2nd Meal End',
};

/** Two punches within this many ms are the same instant (a switch closes and opens at once). */
const SAME_INSTANT_MS = 60 * 1000;
/** An open punch younger than this is still the current shift, even across midnight (API switch rule). */
export const CURRENT_SHIFT_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Row provenance → the chip the handoff shows. Null predates the provenance columns (mobile). */
export function sourceKind(source: string | null | undefined): SourceKind {
  switch ((source ?? '').toUpperCase()) {
    case 'WEB': return 'web';
    case 'KIOSK': return 'kiosk';
    case 'PAYROLL': return 'admin';
    case 'SYSTEM': return 'system';
    default: return 'mobile';
  }
}

export const SOURCE_LABEL: Record<SourceKind, string> = {
  web: 'Web',
  mobile: 'Mobile app',
  kiosk: 'Kiosk',
  admin: 'Admin App',
  system: 'System',
};

/** A row normalized for the model (epoch ms, the job, both sides' sources). */
export interface DayRow {
  id: number;
  inMs: number;
  outMs: number | null;
  type: number;
  job: PortalJob;
  inSource: SourceKind;
  outSource: SourceKind | null;
  /** The optimistic row / side of a punch that hasn't reached the server yet. */
  pendingIn?: boolean;
  pendingOut?: boolean;
}

/* ------------------------------------------------------------------ jobs */

export function jobFromRow(row: PortalHourlyRow): PortalJob {
  return {
    projectID: row.project?.projectID ?? null,
    projectPhaseID: row.projectPhase?.projectPhaseID ?? null,
    accountCodeID: row.account?.accountCodeID ?? null,
    projectCode: row.project?.altProjectID ?? null,
    projectName: row.project?.name ?? null,
    phaseCode: row.projectPhase?.altProjectPhaseID ?? null,
    phaseName: row.projectPhase?.name ?? null,
    costCode: row.account?.altAccountCodeID ?? null,
    costName: row.account?.name ?? null,
  };
}

/** Same charge (project / phase / cost code), ignoring the display labels. */
export function sameJob(a: PortalJob | null | undefined, b: PortalJob | null | undefined): boolean {
  return (a?.projectID ?? null) === (b?.projectID ?? null) &&
    (a?.projectPhaseID ?? null) === (b?.projectPhaseID ?? null) &&
    (a?.accountCodeID ?? null) === (b?.accountCodeID ?? null);
}

/** Non-job time (the tenant's non-job cost code, e.g. 2020PH 1390 "Non-Job Time"): no project. */
export function isOverhead(job: PortalJob | null | undefined): boolean {
  return !job || job.projectID == null;
}

/** The non-job label when the cost code's own name isn't known (the DB name wins: 2020PH "Non-Job Time"). */
export const OVERHEAD_TITLE = 'Non-Job Time';

export function jobTitle(job: PortalJob | null | undefined): string {
  if (isOverhead(job)) return job?.costName || OVERHEAD_TITLE;
  return job!.projectName || job!.projectCode || `Project ${job!.projectID}`;
}

/** `24-118 · 01 Preconstruction · 01-520 Accounts Payable`, or the overhead line. */
export function jobSub(job: PortalJob | null | undefined): string {
  if (isOverhead(job)) return 'Not job related · the default for office time';
  const parts = [
    job!.projectCode ?? '',
    [job!.phaseCode, job!.phaseName].filter(Boolean).join(' '),
    [job!.costCode, job!.costName].filter(Boolean).join(' '),
  ].filter((p) => p !== '');
  return parts.join(' · ');
}

/** `24-118 · 01-520` (activity deltas), or the non-job name (`Non-Job Time`). */
export function jobCode(job: PortalJob | null | undefined): string {
  if (isOverhead(job)) return jobTitle(job);
  return [job!.projectCode, job!.costCode].filter(Boolean).join(' · ') || jobTitle(job);
}

/**
 * The Office / Overhead choice: the tenant's Non-Job account code (mobile
 * hourlyOtherOneAccountCodeSection), else the role's default account code, else none.
 */
export function overheadJob(nonJobAccountCodeID: number | null | undefined, roleDefaultAccountCodeID: number | null | undefined): PortalJob {
  const accountCodeID =
    nonJobAccountCodeID && nonJobAccountCodeID > 0
      ? nonJobAccountCodeID
      : roleDefaultAccountCodeID && roleDefaultAccountCodeID > 0
        ? roleDefaultAccountCodeID
        : null;
  return { projectID: null, projectPhaseID: null, accountCodeID };
}

/**
 * The non-job choice with its cost code's real name / code, taken from any of the
 * employee's rows on that cost code (the API joins AccountCode); unchanged when none has it.
 */
export function namedNonJob(nonJob: PortalJob, jobs: Iterable<PortalJob>): PortalJob {
  if (nonJob.costName || nonJob.accountCodeID == null) return nonJob;
  for (const j of Array.from(jobs)) {
    if (j.projectID == null && j.accountCodeID === nonJob.accountCodeID && j.costName) {
      return { ...nonJob, costName: j.costName, costCode: j.costCode ?? null };
    }
  }
  return nonJob;
}

/* ------------------------------------------------------------------ rows */

export function normalizeRow(row: PortalHourlyRow): DayRow | null {
  const inMs = Date.parse(row.inDateTime);
  if (Number.isNaN(inMs)) return null;
  const outParsed = row.outDateTime ? Date.parse(row.outDateTime) : NaN;
  const outMs = Number.isNaN(outParsed) ? null : outParsed;
  return {
    id: row.timeEntryHourlyID,
    inMs,
    outMs,
    type: row.type,
    job: jobFromRow(row),
    inSource: sourceKind(row.createdSource),
    outSource: outMs == null ? null : sourceKind(row.modifiedSource ?? row.createdSource),
  };
}

/** Synthetic id of the optimistic row a pending punch opens. */
export const PENDING_ROW_ID = -1;

/**
 * Lays a pending (not yet acknowledged) punch over the server rows, so the hero shows the
 * tap at once: a clock in adds an open REG row; a clock out closes the row; a meal / job
 * switch closes the row and opens the next one — all at the tap time.
 */
export function applyPendingPunch(rows: DayRow[], pending: PendingPunch | null): DayRow[] {
  if (!pending) return rows;
  const tap = pending.tapMs;
  // Already reflected by the server rows (the write landed and /self was reloaded): no overlay.
  if (pending.action === 'clockIn') {
    if (rows.some((r) => Math.abs(r.inMs - tap) < 1000)) return rows;
  } else {
    const landed = rows.find((r) => r.id === pending.rowID);
    if (!landed || landed.outMs != null) return rows;
  }
  const out = rows.map((r) => ({ ...r }));
  const target = pending.rowID != null ? out.find((r) => r.id === pending.rowID) : undefined;
  const close = () => {
    if (target && target.outMs == null) {
      target.outMs = tap;
      target.outSource = 'web';
      target.pendingOut = true;
    }
  };
  const open = (type: number, job: PortalJob) => {
    out.push({ id: PENDING_ROW_ID, inMs: tap, outMs: null, type, job, inSource: 'web', outSource: null, pendingIn: true });
  };
  switch (pending.action) {
    case 'clockIn':
      open(1, pending.job ?? { projectID: null, projectPhaseID: null, accountCodeID: null });
      break;
    case 'clockOut':
      close();
      break;
    case 'mealStart':
      close();
      open(TYPE_MEAL, target?.job ?? { projectID: null, projectPhaseID: null, accountCodeID: null });
      break;
    case 'mealEnd': {
      close();
      // The server copies the job of the latest REG row before the meal (mobile ClockManager).
      const lastReg = [...out].filter((r) => r.type !== TYPE_MEAL && r.inMs < tap).sort((a, b) => b.inMs - a.inMs)[0];
      open(1, lastReg?.job ?? target?.job ?? { projectID: null, projectPhaseID: null, accountCodeID: null });
      break;
    }
    case 'jobSwitch':
      close();
      open(1, pending.job ?? target?.job ?? { projectID: null, projectPhaseID: null, accountCodeID: null });
      break;
  }
  return out;
}

/* ------------------------------------------------------------------ the day */

export interface PunchEvent {
  key: string;
  kind: PunchKind;
  atMs: number;
  source: SourceKind;
  pending: boolean;
}

export interface JobSwitchEvent {
  key: string;
  atMs: number;
  from: PortalJob;
  to: PortalJob;
  source: SourceKind;
  pending: boolean;
}

export interface JobSegment {
  key: string;
  fromMs: number;
  /** Null while the segment is still running. */
  toMs: number | null;
  job: PortalJob;
  /** A MEAL row (unpaid): shown between the work segments, never counted as worked. */
  meal: boolean;
  /** The row's minutes so far (worked for a work segment, meal minutes for a meal). */
  workedMin: number;
  /**
   * Work segments: hundredths of an hour, allocated so they add up to the day's WORKED
   * figure (largest remainder, see allocateHundredths). Meals: 0.
   */
  hundredths: number;
  live: boolean;
}

export interface DaySummary {
  state: PunchState;
  /** Today's rows (local day of the in-time; plus a carried-over shift's rows), sorted by in-time. */
  rows: DayRow[];
  /** The open row driving the state (today's, or a carried-over shift's), or null. */
  openRow: DayRow | null;
  /**
   * The current shift started on an earlier local day and is still open within the last
   * 24 h (cross-midnight): its rows are included in `rows` and the hero shows its start day.
   */
  carriedOver: boolean;
  /** An open punch from an EARLIER local day that is not the current shift (forgot to clock out). Not part of today. */
  staleOpenRow: DayRow | null;
  events: PunchEvent[];
  switches: JobSwitchEvent[];
  segments: JobSegment[];
  workedMin: number;
  mealMin: number;
  /** MEAL rows started today. */
  mealCount: number;
  /** In-times of today's meals, in order. */
  mealStarts: number[];
  firstInMs: number | null;
  lastOutMs: number | null;
}

function minutesBetween(a: number, b: number): number {
  return Math.max(0, (b - a) / 60000);
}

/** Minutes → hundredths of an hour, rounded half up (what fmtHours shows: 45 min → 75 → `0.75`). */
export function minutesToHundredths(minutes: number): number {
  return Math.round((Math.max(0, minutes) * 100) / 60);
}

/**
 * Largest-remainder split: each part's hundredths of an hour such that they sum EXACTLY to
 * the rounded total (minutesToHundredths of the sum) — so segment hours add up to the
 * WORKED box (0.01 + 0.01 = 0.02, never 0.02 + 0.01 = 0.02). `targetHundredths` makes them
 * sum to a total computed elsewhere (Day detail's "h paid"), when it is within one
 * hundredth per part of the natural sum.
 */
export function allocateHundredths(minutes: number[], targetHundredths?: number): number[] {
  const exact = minutes.map((m) => (Math.max(0, m) * 100) / 60);
  const floors = exact.map((x) => Math.floor(x));
  const natural = minutesToHundredths(minutes.reduce((a, m) => a + Math.max(0, m), 0));
  // An explicit target (a header total computed elsewhere) wins when it is only a rounding
  // step away — never enough to invent or drop real time.
  const target = targetHundredths != null && Math.abs(targetHundredths - natural) <= minutes.length ? targetHundredths : natural;
  let left = target - floors.reduce((a, b) => a + b, 0);
  if (left < 0) {
    // Take back from the smallest remainders first (never below zero).
    const back = exact.map((x, i) => ({ i, rem: x - floors[i] })).sort((a, b) => a.rem - b.rem || b.i - a.i);
    for (const { i } of back) {
      if (left >= 0) break;
      if (floors[i] > 0) { floors[i] -= 1; left += 1; }
    }
    return floors;
  }
  const order = exact.map((x, i) => ({ i, rem: x - floors[i] })).sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    floors[i] += 1;
    left -= 1;
  }
  return floors;
}

/**
 * Derives Today from the employee's rows. `allRows` may span the whole pay period (the
 * /self payload); only rows whose in-time falls on `todayYmd` in `zone` are today's.
 */
export function deriveDay(allRows: DayRow[], nowMs: number, zone: string, todayYmd: string): DaySummary {
  const sorted = [...allRows].sort((a, b) => a.inMs - b.inMs || a.id - b.id);
  const todays = sorted.filter((r) => localYmd(r.inMs, zone) === todayYmd);
  const openToday0 = [...todays].reverse().find((r) => r.outMs == null) ?? null;

  // A shift that crossed midnight: with nothing open today, the latest open punch from an
  // earlier day that started within the last 24 h is still the CURRENT shift (the API
  // switch accepts it the same way).
  const carried = openToday0
    ? null
    : [...sorted].reverse().find(
        (r) => r.outMs == null && localYmd(r.inMs, zone) < todayYmd && nowMs - r.inMs < CURRENT_SHIFT_WINDOW_MS,
      ) ?? null;
  const openToday = openToday0 ?? carried;
  // The open shift's contiguous chain (REG → MEAL → REG …, each in touching the previous
  // out) back to its first clock-in; the part of it before midnight joins "today".
  let shiftStartMs: number | null = null;
  if (openToday) {
    shiftStartMs = openToday.inMs;
    for (let k = sorted.indexOf(openToday) - 1; k >= 0; k--) {
      const prev = sorted[k];
      if (prev.outMs != null && Math.abs(prev.outMs - shiftStartMs) <= SAME_INSTANT_MS) shiftStartMs = prev.inMs;
      else if (prev.outMs == null || prev.outMs < shiftStartMs - SAME_INSTANT_MS) break;
    }
  }
  const carriedOver = shiftStartMs != null && localYmd(shiftStartMs, zone) < todayYmd;
  const rows = carriedOver
    ? sorted.filter((r) => localYmd(r.inMs, zone) === todayYmd || (r.inMs >= shiftStartMs! && localYmd(r.inMs, zone) < todayYmd))
    : todays;
  // An open punch from an earlier day that is NOT the current shift (forgot to clock out).
  const staleOpenRow = sorted.find((r) => r.outMs == null && r !== carried && localYmd(r.inMs, zone) < todayYmd) ?? null;

  const events: PunchEvent[] = [];
  const switches: JobSwitchEvent[] = [];
  let mealIndex = 0;
  const mealStarts: number[] = [];

  rows.forEach((r, i) => {
    const prev = i > 0 ? rows[i - 1] : null;
    const next = i < rows.length - 1 ? rows[i + 1] : null;
    const touchesPrev = !!prev && prev.outMs != null && Math.abs(prev.outMs - r.inMs) <= SAME_INSTANT_MS;
    const touchesNext = !!next && r.outMs != null && Math.abs(next.inMs - r.outMs) <= SAME_INSTANT_MS;

    if (r.type === TYPE_MEAL) {
      mealIndex += 1;
      mealStarts.push(r.inMs);
      events.push({ key: `${r.id}-in`, kind: mealIndex >= 2 ? 'meal2-start' : 'meal-start', atMs: r.inMs, source: r.inSource, pending: !!r.pendingIn });
      if (r.outMs != null) {
        // A meal end is the MEAL row's out (whether or not a REG row follows it).
        events.push({
          key: `${r.id}-out`, kind: mealIndex >= 2 ? 'meal2-end' : 'meal-end', atMs: r.outMs,
          source: r.outSource ?? r.inSource, pending: !!r.pendingOut,
        });
      }
      return;
    }

    // REG row: its in is a Clock In unless it continues a meal or a job switch.
    if (!touchesPrev) {
      events.push({ key: `${r.id}-in`, kind: 'in', atMs: r.inMs, source: r.inSource, pending: !!r.pendingIn });
    } else if (prev && prev.type !== TYPE_MEAL && !sameJob(prev.job, r.job)) {
      switches.push({ key: `${r.id}-sw`, atMs: r.inMs, from: prev.job, to: r.job, source: r.inSource, pending: !!r.pendingIn });
    }
    // Its out is a Clock Out unless the next row picks up at the same instant.
    if (r.outMs != null && !touchesNext) {
      events.push({ key: `${r.id}-out`, kind: 'out', atMs: r.outMs, source: r.outSource ?? r.inSource, pending: !!r.pendingOut });
    }
  });
  events.sort((a, b) => a.atMs - b.atMs);

  // Segments: one per row, in order — every row boundary (meal start / end, job switch,
  // clock out / back in, the midnight split) starts a new one; meals are their own
  // (unpaid) segments.
  const segments: JobSegment[] = rows.map((r) => ({
    key: `seg-${r.id}-${r.inMs}`,
    fromMs: r.inMs,
    toMs: r.outMs,
    job: r.job,
    meal: r.type === TYPE_MEAL,
    workedMin: minutesBetween(r.inMs, r.outMs ?? nowMs),
    hundredths: 0,
    live: false,
  }));
  const work = segments.filter((g) => !g.meal);
  allocateHundredths(work.map((g) => g.workedMin)).forEach((v, k) => { work[k].hundredths = v; });

  const state: PunchState = openToday ? (openToday.type === TYPE_MEAL ? 'meal' : 'in') : rows.length ? 'done' : 'out';
  if (segments.length && (state === 'in' || state === 'meal')) {
    const last = segments[segments.length - 1];
    if (last.toMs == null) last.live = true;
  }

  let workedMin = 0;
  let mealMin = 0;
  for (const r of rows) {
    const m = minutesBetween(r.inMs, r.outMs ?? nowMs);
    if (r.type === TYPE_MEAL) mealMin += m;
    else workedMin += m;
  }
  const outs = rows.filter((r) => r.outMs != null).map((r) => r.outMs as number);

  return {
    state,
    rows,
    openRow: openToday,
    carriedOver,
    staleOpenRow,
    events,
    switches,
    segments,
    workedMin,
    mealMin,
    mealCount: mealStarts.length,
    mealStarts,
    firstInMs: rows.length ? rows[0].inMs : null,
    lastOutMs: state === 'done' && outs.length ? Math.max(...outs) : null,
  };
}

/* ------------------------------------------------------------------ actions */

export type HeroAction = 'clockIn' | 'clockBackIn' | 'clockOut' | 'startMeal' | 'startSecondMeal' | 'endMeal';

/**
 * The hero's buttons per state (handoff §1): not clocked in → Clock In; clocked out →
 * Clock Back In; on meal → End Meal; clocked in → Clock Out + Start Meal (no meal yet) or
 * Start 2nd Meal (one meal taken AND ≥ the unlock minutes worked) or nothing.
 */
export function heroActions(state: PunchState, mealCount: number, workedMin: number, secondMealUnlockMinutes: number): HeroAction[] {
  switch (state) {
    case 'out': return ['clockIn'];
    case 'done': return ['clockBackIn'];
    case 'meal': return ['endMeal'];
    case 'in':
      if (mealCount === 0) return ['clockOut', 'startMeal'];
      if (mealCount === 1 && workedMin >= secondMealUnlockMinutes) return ['clockOut', 'startSecondMeal'];
      return ['clockOut'];
  }
}

/** Mobile ClockManager.validateMealTime: a meal under 30 minutes asks before ending. */
export const SHORT_MEAL_MINUTES = 30;
export const SHORT_MEAL_MESSAGE =
  'You are required to take a 30 minute lunch, you may only clock back in for an emergency, do you want to clock back in early?';

export function isShortMeal(mealStartMs: number, endMs: number): boolean {
  return Math.abs(endMs - mealStartMs) / 60000 < SHORT_MEAL_MINUTES;
}

/** Why punching is blocked right now ('' = not blocked). Highest priority first. */
export function blockedReason(input: {
  sessionExpired: boolean;
  unavailable: string | null;
  pendingLabel: string | null;
  otherDevice: SourceKind | null;
  weekLock: 'submitted' | 'exported' | null;
}): string {
  if (input.sessionExpired) return 'Sign in again to punch';
  if (input.unavailable) return input.unavailable;
  if (input.pendingLabel) return `Your ${input.pendingLabel} hasn't been sent yet — punching resumes once it is`;
  if (input.otherDevice) return `Clocked in on the ${SOURCE_LABEL[input.otherDevice].toLowerCase()} — choose Continue here above to punch from this browser`;
  if (input.weekLock === 'exported') return "Punching is locked because this week's payroll was exported";
  if (input.weekLock === 'submitted') return "Punching is locked because this week's timesheet was submitted";
  return '';
}

/* ------------------------------------------------------------------ meal compliance */

export type WorkState = 'CA' | 'NV' | 'OTHER';

export function workStateOf(stateAbbr: string | null | undefined): WorkState {
  const s = (stateAbbr ?? '').trim().toUpperCase();
  return s === 'CA' ? 'CA' : s === 'NV' ? 'NV' : 'OTHER';
}

export interface MealRules {
  workState: WorkState;
  /** The tenant's meal-waiver flag for this work state (companyInfo CAMealWaivers / NVMealWaivers). */
  waiversOn: boolean;
  /** CA: the first meal must start before this much of the shift (mealPenaltyThresholdMinutes). */
  penaltyThresholdMinutes: number;
}

/**
 * The flags that become a meal-waiver question at weekly submit (handoff D4), for the
 * clock-out toast and hint only — the API's mealWaiverDates is authoritative.
 * CA: > 5 h worked with no meal, or the first meal started after the 5th hour; > 10 h with
 * fewer than 2 meals. NV: > 8 h with no meal.
 */
export function todayMealFlags(day: Pick<DaySummary, 'workedMin' | 'mealStarts' | 'firstInMs'>, rules: MealRules): string[] {
  if (!rules.waiversOn || day.firstInMs == null) return [];
  const out: string[] = [];
  if (rules.workState === 'CA') {
    const firstMealLate = day.mealStarts.length > 0 && (day.mealStarts[0] - day.firstInMs) / 60000 > rules.penaltyThresholdMinutes;
    if (day.workedMin > rules.penaltyThresholdMinutes && (day.mealStarts.length === 0 || firstMealLate)) out.push('meal1');
    if (day.workedMin > 600 && day.mealStarts.length < 2) out.push('meal2');
  } else if (rules.workState === 'NV') {
    if (day.workedMin > 480 && day.mealStarts.length === 0) out.push('mealNV');
  }
  return out;
}

/** The hero hint line (handoff §1 "Hint line"). */
export function complianceHint(day: DaySummary, rules: MealRules, zone: string): string {
  const w = day.workedMin;
  const m = day.mealStarts;
  const CA = rules.workState === 'CA';
  if (day.state === 'out') {
    return CA ? 'Your first meal is due before the end of your 5th hour of work' : 'Shifts over 8 hours include a meal period';
  }
  if (day.state === 'meal') {
    const start = m.length ? m[m.length - 1] : null;
    return start != null ? `Meal started at ${fmtTime(start, zone)} · unpaid · end it when you're back` : "On meal · unpaid · end it when you're back";
  }
  if (day.state === 'done') {
    return todayMealFlags(day, rules).length
      ? "Heads up: today's meal will be a question when you submit the week"
      : `Shift complete · ${fmtHours(w)} h worked`;
  }
  if (CA) {
    if (!m.length) {
      return w < rules.penaltyThresholdMinutes && day.firstInMs != null
        ? `First meal due by ${fmtTime(day.firstInMs + rules.penaltyThresholdMinutes * 60000, zone)} — before the end of your 5th hour`
        : "No meal yet past your 5th hour — you'll answer this when you submit the week";
    }
    if (m.length === 1) {
      return w < 480
        ? '2nd meal unlocks at 8.00 h worked · needed if your shift runs past 10 h'
        : 'Working past 10 h? Start your 2nd meal before the end of your 10th hour';
    }
    return 'Both meal periods taken today';
  }
  if (!m.length) {
    return w < 480 ? 'Working more than 8 h today? Take a meal period' : "Over 8 h with no meal — you'll answer this when you submit the week";
  }
  return 'Meal period taken today';
}

/** Whether to show the gentle first-meal reminder now (CA, clocked in, no meal yet, past the reminder minutes). */
export function shouldRemindMeal(day: DaySummary, rules: MealRules, reminderMinutes: number): boolean {
  return rules.workState === 'CA' && day.state === 'in' && day.mealCount === 0 && day.workedMin >= reminderMinutes;
}

/* ------------------------------------------------------------------ status strip */

export type StripKind =
  | 'network'
  | 'loadError'
  | 'otherDevice'
  | 'locked'
  | 'noSupervisor'
  | 'lastWeekOpen'
  | 'holiday'
  | 'caughtUp'
  | 'loading';

export interface StripInput {
  loading: boolean;
  /** A punch failed to send for a network reason and is retrying. */
  pendingNetwork: boolean;
  /** Today's data couldn't be loaded (network / server), with Retry. */
  loadError: boolean;
  /** An open punch made elsewhere that the employee hasn't chosen to continue here. */
  otherDevice: boolean;
  weekLock: 'submitted' | 'exported' | null;
  noSupervisor: boolean;
  lastWeekOpen: boolean;
  holidayToday: boolean;
}

/**
 * The status strip's state, highest priority first (handoff §Shell "Status strip"; the
 * submit deadline is overridden — there is no "late"). The strip is always present at
 * 60px: a banner REPLACES its content, never adds a row (D7).
 */
export function selectStrip(input: StripInput): StripKind {
  if (input.pendingNetwork) return 'network';
  if (input.loadError) return 'loadError';
  if (input.loading) return 'loading';
  if (input.otherDevice) return 'otherDevice';
  if (input.weekLock) return 'locked';
  if (input.noSupervisor) return 'noSupervisor';
  if (input.lastWeekOpen) return 'lastWeekOpen';
  if (input.holidayToday) return 'holiday';
  return 'caughtUp';
}

/* ------------------------------------------------------------------ pay periods */

function ymdOf(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
}

/** /payPeriods/getAroundDate answers the raw recordset (PascalCase); accept either casing. */
export function normalizePayPeriods(raw: unknown): PortalPayPeriod[] {
  if (!Array.isArray(raw)) return [];
  const out: PortalPayPeriod[] = [];
  for (const p of raw as Record<string, unknown>[]) {
    const id = Number(p.PayPeriodID ?? p.payPeriodID);
    const startYmd = ymdOf(p.StartDate ?? p.startDate);
    const endYmd = ymdOf(p.EndDate ?? p.endDate);
    if (!Number.isInteger(id) || id <= 0 || !startYmd || !endYmd) continue;
    out.push({ payPeriodID: id, startYmd, endYmd, checkYmd: ymdOf(p.CheckDate ?? p.checkDate) });
  }
  return out.sort((a, b) => (a.startYmd < b.startYmd ? -1 : a.startYmd > b.startYmd ? 1 : 0));
}

/** The period containing `ymd`, and the one before it (last week). */
export function currentAndPrevious(periods: PortalPayPeriod[], ymd: string): { current: PortalPayPeriod | null; previous: PortalPayPeriod | null } {
  const idx = periods.findIndex((p) => p.startYmd <= ymd && ymd <= p.endYmd);
  if (idx < 0) return { current: null, previous: null };
  return { current: periods[idx], previous: idx > 0 ? periods[idx - 1] : null };
}

/* ------------------------------------------------------------------ coming up */

export interface UpcomingRow {
  key: string;
  ymd: string;
  title: string;
  sub: string;
}

function isStatus(r: PortalTimeOffRequest, s: string): boolean {
  return (r.status ?? '').toLowerCase() === s;
}

/**
 * The Coming up card: the next payday, upcoming approved / pending time off, and the next
 * company holidays — soonest first, at most `limit`.
 */
export function buildUpcoming(input: {
  todayYmd: string;
  periods: PortalPayPeriod[];
  requests: PortalTimeOffRequest[];
  holidays: PortalHoliday[];
  supervisorName: string | null;
  limit?: number;
}): UpcomingRow[] {
  const rows: UpcomingRow[] = [];
  const payday = input.periods
    .filter((p) => p.checkYmd && p.checkYmd >= input.todayYmd && p.endYmd < (p.checkYmd as string))
    .sort((a, b) => ((a.checkYmd as string) < (b.checkYmd as string) ? -1 : 1))[0];
  if (payday?.checkYmd) {
    rows.push({ key: `pay-${payday.payPeriodID}`, ymd: payday.checkYmd, title: 'Payday', sub: `Covers ${shortRange(payday.startYmd, payday.endYmd)}` });
  }
  for (const r of input.requests) {
    const start = (r.startDate ?? '').slice(0, 10);
    if (!start || start <= input.todayYmd) continue;
    const approved = isStatus(r, 'approved');
    if (!approved && !isStatus(r, 'pending')) continue;
    rows.push({
      key: `to-${r.timeOffRequestID}`,
      ymd: start,
      title: `${r.categoryName} · ${Number(r.totalHours ?? 0).toFixed(2)} h`,
      sub: `TO-${r.timeOffRequestID} · ${approved ? 'Approved' : `Pending ${input.supervisorName ?? 'approval'}`}`,
    });
  }
  for (const h of input.holidays) {
    const d = (h.holidayDate ?? '').slice(0, 10);
    if (!d || d <= input.todayYmd) continue;
    const eligible = h.eligibility ? h.eligibility.isEligible : true;
    rows.push({
      key: `hol-${h.holidayCalendarID}-${d}`,
      ymd: d,
      title: h.name,
      sub: eligible ? `Company holiday · ${Number(h.paidHoursIfNotWorked ?? 0).toFixed(2)} h paid` : 'Company holiday · not paid for you',
    });
  }
  rows.sort((a, b) => (a.ymd < b.ymd ? -1 : a.ymd > b.ymd ? 1 : 0));
  return rows.slice(0, input.limit ?? 4);
}

/** `Oct 5 – Oct 11`. */
export function shortRange(startYmd: string, endYmd: string): string {
  const f = (ymd: string) => new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${f(startYmd)} – ${f(endYmd)}`;
}

/** Pending time-off requests (the Time Off nav badge). */
export function pendingTimeOffCount(requests: PortalTimeOffRequest[]): number {
  return requests.filter((r) => isStatus(r, 'pending')).length;
}

/* ------------------------------------------------------------------ recent activity */

export interface ActivityItem {
  key: string;
  atMs: number;
  title: string;
  source: SourceKind;
  pending: boolean;
  delta?: { was: string; now: string };
}

/** Today's punches and job switches as activity rows, newest first (the full feed is App-4). */
export function todaysActivity(day: Pick<DaySummary, 'events' | 'switches'>): ActivityItem[] {
  const items: ActivityItem[] = [
    ...day.events.map((e) => ({ key: e.key, atMs: e.atMs, title: `${PUNCH_LABEL[e.kind]} punched`, source: e.source, pending: e.pending })),
    ...day.switches.map((s) => ({
      key: s.key, atMs: s.atMs, title: 'Switched job', source: s.source, pending: s.pending,
      delta: { was: jobCode(s.from), now: jobCode(s.to) },
    })),
  ];
  return items.sort((a, b) => b.atMs - a.atMs);
}
